import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAudit } from '../hooks/useAudit';
import Button from '../components/Button';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { format } from 'date-fns';
import { trackEvent } from '../config/analytics';

// Safe date formatter
const safeFormatDate = (dateValue, formatStr = 'PPP') => {
  try {
    if (!dateValue) return '-';
    let date;
    if (dateValue?.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return '-';
    }
    if (isNaN(date.getTime())) return '-';
    return format(date, formatStr);
  } catch (err) {
    return '-';
  }
};

const AuditResultsPage = () => {
  const { auditId } = useParams();
  const { audit, loading, error } = useAudit(auditId);
  const navigate = useNavigate();
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [expandedViolations, setExpandedViolations] = useState({});

  useEffect(() => {
    if (audit) {
      trackEvent('audit_results_viewed', { auditId, status: audit.status });
    }
  }, [audit, auditId]);

  const toggleViolation = (violationId) => {
    setExpandedViolations(prev => ({
      ...prev,
      [violationId]: !prev[violationId]
    }));
  };

  const getSeverityBadge = (impact) => {
    const badges = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      serious: 'bg-orange-100 text-orange-800 border-orange-300',
      moderate: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      minor: 'bg-blue-100 text-blue-800 border-blue-300',
    };
    return badges[impact] || badges.minor;
  };

  const getSeverityIcon = (impact) => {
    const icons = {
      critical: '🔴',
      serious: '🟠',
      moderate: '🟡',
      minor: '🔵',
    };
    return icons[impact] || '🔵';
  };

  const exportReport = () => {
    trackEvent('report_exported', { auditId });

    const reportData = {
      audit: {
        id: audit.id,
        url: audit.url,
        date: safeFormatDate(audit.createdAt),
        status: audit.status,
      },
      summary: audit.summary,
      violations: audit.violations,
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accessibility-report-${audit.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner size="lg" text="Loading audit results..." />
        </div>
      </div>
    );
  }

  if (error || !audit) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Audit Not Found</h2>
              <p className="text-gray-600 mb-6">{error || 'The audit you are looking for does not exist.'}</p>
              <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (audit.status === 'processing') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <div className="text-center py-12">
              <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Audit in Progress</h2>
              <p className="text-gray-600">Please wait while we scan your website...</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const filteredViolations = selectedSeverity === 'all' 
    ? audit.violations 
    : audit.violations?.filter(v => v.impact === selectedSeverity);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Audit Results</h1>
              <p className="mt-2 text-gray-600">{audit.url}</p>
              <p className="text-sm text-gray-500">
                {safeFormatDate(audit.createdAt, 'PPP p')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" onClick={exportReport}>
                Export Report
              </Button>
              <Button onClick={() => navigate('/audit/new')}>
                New Audit
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="text-center">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Critical Issues</h3>
            <p className="text-4xl font-bold text-red-600">{audit.summary?.critical || 0}</p>
          </Card>
          <Card className="text-center">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Serious Issues</h3>
            <p className="text-4xl font-bold text-orange-600">{audit.summary?.serious || 0}</p>
          </Card>
          <Card className="text-center">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Moderate Issues</h3>
            <p className="text-4xl font-bold text-yellow-600">{audit.summary?.moderate || 0}</p>
          </Card>
          <Card className="text-center">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Minor Issues</h3>
            <p className="text-4xl font-bold text-blue-600">{audit.summary?.minor || 0}</p>
          </Card>
        </div>

        {/* Overall Score */}
        {(audit.score !== undefined || audit.summary?.score !== undefined) && (
          <Card className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Accessibility Score</h3>
                <p className="text-gray-600">Based on WCAG 2.2 Level AA standards</p>
                {(audit.wcagLevel || audit.summary?.wcagLevel) && (
                  <p className="text-sm text-gray-500 mt-2">
                    WCAG Level: <span className="font-semibold">{audit.wcagLevel || audit.summary?.wcagLevel}</span>
                  </p>
                )}
                {audit.summary?.levelAACompliant !== undefined && (
                  <p className={`text-sm mt-1 font-medium ${audit.summary.levelAACompliant ? 'text-green-600' : 'text-red-600'}`}>
                    {audit.summary.levelAACompliant ? '✓ Level AA Compliant' : '✗ Not Level AA Compliant'}
                  </p>
                )}
              </div>
              <div className="text-center">
                <div className={`text-6xl font-bold ${
                  (audit.score || audit.summary?.score) >= 90 ? 'text-green-600' :
                  (audit.score || audit.summary?.score) >= 70 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {audit.score || audit.summary?.score || 0}
                </div>
                <p className="text-sm text-gray-500 mt-2">out of 100</p>
              </div>
            </div>
          </Card>
        )}

        {/* Warnings Section */}
        {audit.warnings && audit.warnings.length > 0 && (
          <Card className="mb-8 border-l-4 border-yellow-400">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ⚠️ Warnings ({audit.warnings.length})
            </h3>
            <div className="space-y-3">
              {audit.warnings.map((warning, index) => (
                <div key={index} className="bg-yellow-50 p-4 rounded-lg">
                  <p className="font-medium text-yellow-900">{warning.description}</p>
                  {warning.recommendation && (
                    <p className="text-sm text-yellow-800 mt-2">
                      <span className="font-semibold">Recommendation:</span> {warning.recommendation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Passes Section */}
        {audit.passes && audit.passes.length > 0 && (
          <Card className="mb-8 border-l-4 border-green-400">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Passed Checks ({audit.passes.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {audit.passes.map((pass, index) => (
                <div key={index} className="bg-green-50 p-3 rounded-lg text-sm text-green-800">
                  {typeof pass === 'string' ? pass : (pass?.description || pass?.id || JSON.stringify(pass))}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Filter Buttons */}
        <Card className="mb-6">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedSeverity('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedSeverity === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Issues ({audit.violations?.length || 0})
            </button>
            <button
              onClick={() => setSelectedSeverity('critical')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedSeverity === 'critical'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              Critical ({audit.summary?.critical || 0})
            </button>
            <button
              onClick={() => setSelectedSeverity('serious')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedSeverity === 'serious'
                  ? 'bg-orange-600 text-white'
                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
              }`}
            >
              Serious ({audit.summary?.serious || 0})
            </button>
            <button
              onClick={() => setSelectedSeverity('moderate')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedSeverity === 'moderate'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
              }`}
            >
              Moderate ({audit.summary?.moderate || 0})
            </button>
            <button
              onClick={() => setSelectedSeverity('minor')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedSeverity === 'minor'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              Minor ({audit.summary?.minor || 0})
            </button>
          </div>
        </Card>

        {/* Violations List */}
        <div className="space-y-4">
          {!filteredViolations || filteredViolations.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No {selectedSeverity !== 'all' ? selectedSeverity : ''} issues found!
                </h3>
                <p className="text-gray-600">
                  {selectedSeverity === 'all' 
                    ? 'Your website passed all accessibility tests.'
                    : `No ${selectedSeverity} issues were detected.`}
                </p>
              </div>
            </Card>
          ) : (
            filteredViolations.map((violation, index) => (
              <Card key={index} className="border-l-4" style={{ borderColor: violation.impact === 'critical' ? '#DC2626' : violation.impact === 'serious' ? '#EA580C' : violation.impact === 'moderate' ? '#CA8A04' : '#2563EB' }}>
                <div 
                  className="cursor-pointer"
                  onClick={() => toggleViolation(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{getSeverityIcon(violation.impact)}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase border ${getSeverityBadge(violation.impact)}`}>
                          {violation.impact}
                        </span>
                        <span className="text-sm text-gray-500">
                          {violation.nodes?.length || 0} instances
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {violation.description}
                      </h3>
                      <p className="text-gray-600 text-sm">{violation.help}</p>
                    </div>
                    <svg
                      className={`w-6 h-6 text-gray-400 transition-transform ${expandedViolations[index] ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {expandedViolations[index] && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">WCAG Reference:</h4>
                      <a
                        href={violation.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-700 text-sm"
                      >
                        {violation.id} - Learn more →
                      </a>
                    </div>

                    {violation.nodes && violation.nodes.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Affected Elements ({violation.nodes.length}):
                        </h4>
                        <div className="space-y-3">
                          {violation.nodes.slice(0, 5).map((node, nodeIndex) => (
                            <div key={nodeIndex} className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-sm font-mono text-gray-800 mb-2 break-all">
                                {node.html}
                              </p>
                              {node.failureSummary && (
                                <p className="text-sm text-red-600">{node.failureSummary}</p>
                              )}
                            </div>
                          ))}
                          {violation.nodes.length > 5 && (
                            <p className="text-sm text-gray-500 italic">
                              ... and {violation.nodes.length - 5} more instances
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">💡 How to Fix:</h4>
                      <p className="text-sm text-blue-800">{violation.help}</p>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/history')}>
            View All Audits
          </Button>
          <Button onClick={() => navigate('/audit/new')}>
            Run Another Audit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditResultsPage;
